/**
 * ============================================================================
 * ProformaSimulatorPanel — S19.5 UI Predictiva
 * ============================================================================
 * Panel del Simulador Pro-Forma de Landed Cost.
 *
 * Flujo:
 *   1. Lista simulaciones (drafts y guardadas) en columna izquierda.
 *   2. Modal "Clonar desde Embarque" → invoca RPC clonar_embarque_a_proforma.
 *   3. Al seleccionar una simulación, hidrata sliders desde sim_params del
 *      payload JSONB y ejecuta calcLandedCostV2 IN-MEMORY en cada cambio.
 *   4. Muestra delta vs baseline (verde si baja, rojo si sube).
 *   5. "Guardar" persiste sim_params actualizados (debounced).
 *
 * Performance: el recálculo es 100% client-side via useMemo. No hay roundtrip
 * a la DB durante el ajuste de sliders → UI sin lag.
 * ============================================================================
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowUpRight,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  Info,
  Loader2,
  
  Package,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings2,
  Snowflake,
  Sparkles,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { TablePagination, usePagination } from "@/components/TablePagination";
import {
  PRESETS_BY_INCOTERM,
  INCOTERM_LABELS,
  DEFAULT_SOBRECOSTOS,
  inferScope,
  type Incoterm,
} from "@/lib/expensePresets";
import { isTrmOutdated, formatTrmDate } from "@/lib/businessDays";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { EmbarqueCombobox } from "@/components/admin/EmbarqueCombobox";
import { ExpenseManagerSheet } from "@/components/ExpenseManagerSheet";
import { ItemManagerSheet } from "@/components/ItemManagerSheet";
import { MonedaSheet } from "@/components/MonedaSheet";
import { NewSimulationWizard } from "@/components/NewSimulationWizard";
import {
  type UiMode,
  loadUiMode,
  saveUiMode,
  shouldForceAdvanced,
} from "@/lib/simulatorUiMode";
import { Switch } from "@/components/ui/switch";
import {
  useProformaSimulations,
  useProformaSimulation,
  useClonarEmbarqueAProforma,
  useUpdateSimulationPayload,
  useDeleteSimulation,
  type ProformaPayload,
  type ProformaPayloadV3,
  type ProformaSimulation,
  type SimulatedGasto,
  type SimulatedGastoOverride,
  type SimulatedItem,
  type SimulatedItemOverride,
  type ProformaComercial,
  isPayloadV3,
} from "@/hooks/useProformaSimulations";
import {
  type CostEngineInput,
  type EngineGastoInput,
  type EngineItemInput,
} from "@/lib/landedCostEngine";
import {
  useLandedCostPreview,
  type LandedCostPreviewInput,
  type LandedCostPreviewResult,
} from "@/hooks/useLandedCostPreview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { exportProformaToExcel, type ProformaRow } from "@/lib/exportProforma";
import { useProveedores } from "@/hooks/useProveedores";
import { useTRM } from "@/hooks/useTRM";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQueryClient, useQuery } from "@tanstack/react-query";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtCOP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const fmtPct = (n: number) =>
  `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;


const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function generateClientUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function ensureUuid(id: string | null | undefined): string {
  return id && UUID_RE.test(id) ? id : generateClientUUID();
}

/** Pequeño icono con tooltip explicativo. */
function InfoHint({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label="Más información"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Ajusta un CostEngineInput aplicando:
 *   1) overrides a ítems base (cantidad/precio/peso) + items nuevos
 *   2) overrides de %arancel y %IVA por itemId (compliance map)
 *   3) overrides al monto de gastos existentes (sim_params.gastos_editados)
 *   4) inyección de gastos imprevistos (sim_params.gastos_nuevos)
 *   5) ajuste TRM proporcional sobre trmMap y trmAduana
 *   6) ajuste de flete sobre los gastos de base "Peso" (post-merge)
 *
 * Sin mutar el original.
 */
function applySimParams(
  input: CostEngineInput,
  gastosEditados: Record<string, SimulatedGastoOverride> = {},
  gastosNuevos: SimulatedGasto[] = [],
  itemsEditados: Record<string, SimulatedItemOverride> = {},
  itemsNuevos: SimulatedItem[] = [],
  arancelOverrides: Record<string, number> = {},
  ivaOverrides: Record<string, number> = {},
  beneficioOverrides: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA"> = {},
  trmOverrides: Record<string, number> = {},
): CostEngineInput {
  // Centro de Monedas: aplicar overrides absolutos por moneda sobre trmMap.
  const newTrmMap: Record<string, number> = { ...input.trmMap };
  for (const [code, rate] of Object.entries(trmOverrides ?? {})) {
    const k = code.toUpperCase();
    if (k === "COP") continue;
    if (Number.isFinite(rate) && Number(rate) > 0) newTrmMap[k] = Number(rate);
  }
  let newTrmAduana = input.trmAduana;
  // Si el usuario sobrescribe USD, esa pasa a ser la TRM aduana proyectada
  // (afecta conversión de ítems USD en el RPC).
  if (trmOverrides?.USD != null && Number(trmOverrides.USD) > 0) {
    newTrmAduana = Number(trmOverrides.USD);
  }

  // 1) Aplicar overrides a items base + recálculo de sub_total
  const baseItemsAjustados: EngineItemInput[] = input.items.map((it) => {
    const ov = itemsEditados[it.id];
    const benOv = beneficioOverrides[it.id];
    if (!ov && !benOv) return it;
    const cantidad = ov?.cantidad ?? Number(it.cantidad ?? 0);
    const precio = ov?.precio_unitario ?? Number(it.precio_unitario ?? 0);
    const subTotalRecalc = Number(cantidad) * Number(precio);
    return {
      ...it,
      cantidad,
      precio_unitario: precio,
      sub_total: ov?.sub_total ?? subTotalRecalc,
      peso_bruto_kg: ov?.peso_bruto_kg ?? it.peso_bruto_kg,
      volumen_cbm: ov?.volumen_cbm ?? (it as { volumen_cbm?: number | null }).volumen_cbm ?? null,
      aplica_tlc: ov?.aplica_tlc ?? it.aplica_tlc,
      beneficio_aduanero: benOv ?? ov?.beneficio_aduanero ?? it.beneficio_aduanero,
      moneda: (ov?.moneda ?? (it as { moneda?: string | null }).moneda) ?? null,
      hs_code: (((ov as { hs_code?: string | null } | undefined)?.hs_code ?? (it as { hs_code?: string | null }).hs_code) ?? "").toString().trim() || null,
      grados_alcoholicos: ov?.grados_alcoholicos ?? (it as { grados_alcoholicos?: number | null }).grados_alcoholicos ?? null,
      litros_por_unidad: ov?.litros_por_unidad ?? (it as { litros_por_unidad?: number | null }).litros_por_unidad ?? null,
      tarifa_ico: ov?.tarifa_ico ?? (it as { tarifa_ico?: number | null }).tarifa_ico ?? null,
    } as EngineItemInput;
  });

  // 2) Inyectar items nuevos
  const itemsInyectados: EngineItemInput[] = itemsNuevos.map((it) => ({
    id: it.id,
    documento_id: it.documento_id,
    sku: it.sku ?? null,
    descripcion: it.descripcion ?? null,
    cantidad: Number(it.cantidad ?? 0),
    precio_unitario: Number(it.precio_unitario ?? 0),
    sub_total: Number(it.sub_total ?? Number(it.cantidad ?? 0) * Number(it.precio_unitario ?? 0)),
    peso_bruto_kg: Number(it.peso_bruto_kg ?? 0),
    volumen_cbm: it.volumen_cbm ?? null,
    aplica_tlc: it.aplica_tlc ?? false,
    beneficio_aduanero: beneficioOverrides[it.id] ?? it.beneficio_aduanero ?? "GENERAL",
    moneda: (it.moneda ?? "USD").toString().toUpperCase(),
    hs_code: (it.hs_code ?? "").toString().trim() || null,
    grados_alcoholicos: it.grados_alcoholicos ?? null,
    litros_por_unidad: it.litros_por_unidad ?? null,
    tarifa_ico: it.tarifa_ico ?? null,
  } as EngineItemInput));

  const newItems: EngineItemInput[] = [...baseItemsAjustados, ...itemsInyectados];

  // 3) Compliance: combinar mapas base con overrides.
  // Si el ítem nuevo NO tiene override explícito de % arancel/IVA, dejamos
  // que el RPC haga el lookup en arancel_1881 a partir de hs_code (no
  // forzamos default 19% → permite que el motor devuelva el IVA real).
  const newArancelMap: Record<string, number> = { ...input.compliance.arancelPctByItemId, ...arancelOverrides };
  const newIvaMap: Record<string, number> = { ...input.compliance.ivaPctByItemId, ...ivaOverrides };

  // 4) Aplicar overrides a gastos existentes (monto/base/iva/concepto/moneda/inhabilitado)
  // FIX gastos-fantasma — Antes solo se propagaban monto, iva_local y base_prorrateo.
  // Si el usuario inhabilitaba un gasto base (aplica_costeo_inventario=false),
  // renombraba concepto o cambiaba moneda en el sheet, el override se registraba
  // en gastosEditados pero applySimParams lo descartaba al foldear → al persistir
  // V3 el flag se perdía y el gasto "reaparecía" habilitado tras el autosave.
  const baseAjustada: EngineGastoInput[] = input.gastos.map((g) => {
    const ov = gastosEditados[g.id];
    if (!ov) return g;
    const next: EngineGastoInput = { ...g };
    if (ov.monto !== undefined && Number.isFinite(Number(ov.monto))) {
      next.monto = Number(ov.monto) + Number(ov.iva_local ?? 0);
    } else if (ov.iva_local !== undefined) {
      next.monto = Number(g.monto ?? 0) + Number(ov.iva_local ?? 0);
    }
    if (ov.base_prorrateo) next.base_prorrateo = ov.base_prorrateo as EngineGastoInput["base_prorrateo"];
    if (ov.concepto !== undefined) next.concepto = ov.concepto;
    if (ov.moneda !== undefined) next.moneda = String(ov.moneda).toUpperCase();
    if (ov.aplica_costeo_inventario !== undefined) {
      next.aplica_costeo_inventario = ov.aplica_costeo_inventario;
    }
    return next;
  });

  // 5) Inyectar gastos imprevistos del escenario.
  // B-03 — La UI bloquea aplicar simulaciones con SIN_CLASIFICAR; si por
  // un edge case llega aquí, lo coercemos a "Valor" para no romper el motor.
  const inyectados: EngineGastoInput[] = gastosNuevos
    .filter(
      (g) => g.concepto.trim() &&
        Number.isFinite(Number(g.monto ?? 0)) &&
        Number.isFinite(Number(g.iva_local ?? 0)),
    )
    .map((g) => {
      const baseSafe =
        g.base_prorrateo === "Ninguna" || g.base_prorrateo === "SIN_CLASIFICAR"
          ? "Valor"
          : g.base_prorrateo;
      return {
        id: ensureUuid(g.id),
        concepto: g.concepto,
        monto: Number(g.monto) + Number(g.iva_local ?? 0),
        moneda: g.moneda,
        base_prorrateo: baseSafe,
        aplica_costeo_inventario:
          g.base_prorrateo === "Ninguna" ? false : g.aplica_costeo_inventario ?? true,
      };
    });

  // FIX TRM-OVERRIDE — Centro de Monedas debe imponerse sobre `trm_pago`
  // capturado del documento original. Sin esto, gastos en USD que ya tenían
  // un trm_pago histórico (p.ej. 3621.86) ignoraban el override del usuario
  // (p.ej. 4150) → la conversión a COP usaba la TRM vieja.
  const newGastosPreOverride: EngineGastoInput[] = [...baseAjustada, ...inyectados];
  const newGastos: EngineGastoInput[] = newGastosPreOverride.map((g) => {
    const code = (g.moneda ?? "").toString().toUpperCase();
    if (!code || code === "COP") return g;
    const ovRate = trmOverrides?.[code];
    if (Number.isFinite(ovRate) && Number(ovRate) > 0) {
      return { ...g, trm_pago: Number(ovRate) };
    }
    return g;
  });

  return {
    ...input,
    items: newItems,
    trmMap: newTrmMap,
    trmAduana: newTrmAduana,
    gastos: newGastos,
    compliance: {
      arancelPctByItemId: newArancelMap,
      ivaPctByItemId: newIvaMap,
    },
  };
}

/**
 * Convierte un `CostEngineInput` (snapshot persistido en simulaciones_proforma)
 * en el payload RAW que consume el RPC `calculate_landed_cost_preview`.
 * El servidor enriquecerá arancel/IVA y trm_map automáticamente, pero el
 * snapshot ya trae overrides de compliance — los pasamos como `iva_override`
 * para preservar la intención del usuario.
 */
function costEngineInputToRaw(input: CostEngineInput): LandedCostPreviewInput {
  const docMonedaById = new Map(input.documents.map((d) => [d.id, (d.moneda ?? "COP").toString()]));
  const trmMap = input.trmMap ?? {};
  const trmAduana = Number(input.trmAduana ?? 0);

  return {
    trm_aduana: trmAduana,
    trm_map: (() => {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(trmMap)) {
        const code = k.toUpperCase();
        if (code === "COP") continue;
        const num = Number(v);
        if (Number.isFinite(num) && num > 0) out[code] = num;
      }
      return out;
    })(),
    items: input.items.map((it) => {
      const ivaOv = input.compliance.ivaPctByItemId[it.id];
      const beneficio =
        it.beneficio_aduanero
        ?? (it.aplica_tlc ? "TLC_PURO" : "GENERAL");

      const monedaItem = (((it as { moneda?: string | null }).moneda) || docMonedaById.get(it.documento_id) || "COP").toString().toUpperCase();

      return {
        id: it.id,
        documento_id: it.documento_id,
        moneda_doc: monedaItem,
        cantidad: Number(it.cantidad ?? 0),
        peso_bruto_kg: Number(it.peso_bruto_kg ?? 0),
        sub_total: Number(it.sub_total ?? 0),
        hs_code: (((it as { hs_code?: string | null }).hs_code ?? "").toString().trim()) || null,
        aplica_tlc: !!it.aplica_tlc,
        iva_override: ivaOv != null ? Number(ivaOv) : null,
        beneficio_aduanero: beneficio,
        grados_alcoholicos: (it as { grados_alcoholicos?: number | null }).grados_alcoholicos ?? null,
        litros_por_unidad: (it as { litros_por_unidad?: number | null }).litros_por_unidad ?? null,
        tarifa_ico: (it as { tarifa_ico?: number | null }).tarifa_ico ?? null,
      };
    }),
    gastos: input.gastos.map((g) => {
      // ✅ Fase 1.1 — Soporte CBM: mapeamos a "Valor" para el motor pero
      // preservamos `base_declarada` para trazabilidad en allocations.
      const isCBM = g.base_prorrateo === "CBM";
      return {
        // ✅ Fase 1.2 — TRACE-01: enviamos id del gasto para que el RPC
        // devuelva allocations correlacionadas (gasto_id != null).
        id: g.id ?? null,
        monto: Number(g.monto ?? 0),
        moneda: g.moneda,
        base_prorrateo: isCBM ? "Valor" : (g.base_prorrateo === "Peso" ? "Peso" : "Valor"),
        base_declarada: isCBM ? "CBM" : (g.base_prorrateo as "Peso" | "Valor"),
        trm_pago: g.trm_pago != null ? Number(g.trm_pago) : null,
        aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
        // Fase B — Forward del flag DIAN. El RPC lo usa para separar la
        // base gravable (FOB + sólo gastos internacionales) del CIF financiero.
        incluir_en_base_gravable: g.incluir_en_base_gravable ?? null,
      };
    }),
  };
}

function hydrateFxRates(input: CostEngineInput, latestTrmMap: Record<string, number>, trmBaseEffective: number): CostEngineInput {
  const snapshotMap = input.trmMap ?? {};
  const nextMap: Record<string, number> = { ...snapshotMap, COP: 1 };

  // Incluimos SIEMPRE todas las monedas conocidas en trm_historico,
  // no solo las que ya estaban en el snapshot. Esto garantiza que ítems o
  // gastos nuevos del escenario (en monedas que no figuraban en el embarque
  // base, p. ej. GBP) tengan TRM disponible en lugar de quedar "sin TRM".
  for (const [currency, rate] of Object.entries(latestTrmMap ?? {})) {
    const code = (currency ?? "").toUpperCase();
    if (!code || code === "COP") continue;
    const numericRate = Number(rate ?? 0);
    if (numericRate > 0) nextMap[code] = numericRate;
  }

  // USD usa la TRM efectiva (slider de ajuste se aplica más adelante en applySimParams)
  if (trmBaseEffective > 0) nextMap.USD = trmBaseEffective;
  else if (!(Number(nextMap.USD ?? 0) > 0)) {
    nextMap.USD = Number(latestTrmMap.USD ?? snapshotMap.USD ?? 0);
  }

  nextMap.COP = 1;
  return { ...input, trmMap: nextMap };
}

// ── Subcomponente: tarjeta KPI ──────────────────────────────────────────────
function KpiTile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "good" | "bad";
}) {
  const toneCls =
    tone === "good"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "bad"
        ? "text-red-600 dark:text-red-400"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`text-xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

// ── Modal: Clonar desde Embarque ────────────────────────────────────────────
function CloneModal({
  open,
  onOpenChange,
  onCloned,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCloned: (newId: string) => void;
}) {
  const [embarqueId, setEmbarqueId] = useState<string | null>(null);
  const [nombre, setNombre] = useState<string>("");
  const { toast } = useToast();
  const clonar = useClonarEmbarqueAProforma();

  useEffect(() => {
    if (!open) {
      setEmbarqueId(null);
      setNombre("");
    }
  }, [open]);

  const handleClone = async () => {
    if (!embarqueId) {
      toast({ title: "Selecciona un embarque", variant: "destructive" });
      return;
    }
    try {
      const newId = await clonar.mutateAsync({
        embarqueId,
        nombre: nombre.trim() || undefined,
      });
      toast({
        title: "Simulación creada",
        description: "Ajusta los parámetros con los sliders.",
      });
      onOpenChange(false);
      onCloned(newId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo clonar", description: msg, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva simulación pro-forma</DialogTitle>
          <DialogDescription>
            Clona los gastos, ítems y compliance de un embarque real para
            simular escenarios sin tocar la operación.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Embarque base</Label>
            <EmbarqueCombobox
              value={embarqueId}
              onChange={setEmbarqueId}
              placeholder="Buscar embarque..."
            />
          </div>
          <div className="space-y-2">
            <Label>Nombre (opcional)</Label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Escenario Q3 con TRM al alza"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleClone} disabled={clonar.isPending || !embarqueId}>
            {clonar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear simulación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tipos auxiliares para validación pre-export ─────────────────────────────
type ExportWarning = {
  code: "W_HS_DUPLICATED" | "W_GASTO_ZERO" | "W_NO_WEIGHT" | "W_NO_SKU";
  message: string;
  details: string[];
};

// ── Componente principal ────────────────────────────────────────────────────
export default function ProformaSimulatorPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("simId");

  const [cloneOpen, setCloneOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expenseSheetOpen, setExpenseSheetOpen] = useState(false);
  const [itemSheetOpen, setItemSheetOpen] = useState(false);
  // ── UI Mode (Rápido/Avanzado) — solo afecta visibilidad, no cálculos ──
  const [uiMode, setUiMode] = useState<UiMode>(() => loadUiMode());
  const handleSetUiMode = (next: UiMode) => {
    setUiMode(next);
    saveUiMode(next);
  };
  const [exportBusy, setExportBusy] = useState(false);
  const [exportWarnings, setExportWarnings] = useState<ExportWarning[] | null>(null);

  // Lista: búsqueda, filtro de estado y ordenamiento
  const [simSearch, setSimSearch] = useState("");
  const [simStatusFilter, setSimStatusFilter] = useState<"all" | "draft" | "saved" | "materialized">("all");
  const [simSort, setSimSort] = useState<"updated_desc" | "updated_asc" | "name_asc" | "name_desc">("updated_desc");

  const { data: simulations = [], isLoading: simsLoading } = useProformaSimulations();
  const { data: current } = useProformaSimulation(selectedId);
  const { data: proveedores = [] } = useProveedores();
  const updateMut = useUpdateSimulationPayload();
  const deleteMut = useDeleteSimulation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { trm: trmMarket, trmMap: latestTrmMap } = useTRM();

  // Fase 5B — Antigüedad de la TRM aduana. Se usa la fecha del embarque
  // base (fecha_llegada_real, en su defecto created_at) para detectar si la
  // TRM oficial DIM ya está desactualizada y advertir al usuario.
  const embarqueBaseId = (current as { payload?: { embarque_base?: { id?: string | null } | null } } | undefined)
    ?.payload?.embarque_base?.id ?? null;
  const { data: embarqueBaseMeta } = useQuery({
    queryKey: ["proforma-embarque-trm-date", embarqueBaseId],
    enabled: !!embarqueBaseId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!embarqueBaseId) return null;
      const { data } = await supabase
        .from("embarques")
        .select("fecha_llegada_real, created_at")
        .eq("id", embarqueBaseId)
        .maybeSingle();
      return data ?? null;
    },
  });
  const trmAduanaDate = embarqueBaseMeta?.fecha_llegada_real ?? embarqueBaseMeta?.created_at ?? null;
  const trmIsOutdated = isTrmOutdated(trmAduanaDate, 5);

  // Estado en memoria para gastos editados / nuevos (sincronizado con sim_params)
  const [gastosEditados, setGastosEditados] = useState<Record<string, SimulatedGastoOverride>>({});
  const [gastosNuevos, setGastosNuevos] = useState<SimulatedGasto[]>([]);
  // S19.10: ítems editados / nuevos + overrides de %arancel/%IVA
  const [itemsEditados, setItemsEditados] = useState<Record<string, SimulatedItemOverride>>({});
  const [itemsNuevos, setItemsNuevos] = useState<SimulatedItem[]>([]);
  const [arancelOverrides, setArancelOverrides] = useState<Record<string, number>>({});
  const [ivaOverrides, setIvaOverrides] = useState<Record<string, number>>({});
  // Fase 1.3 — overrides explícitos de régimen aduanero por itemId
  const [beneficioOverrides, setBeneficioOverrides] = useState<
    Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA">
  >({});
  // Centro de Monedas — overrides de TRM por moneda (vacío = mercado).
  const [trmOverrides, setTrmOverrides] = useState<Record<string, number>>({});
  const [monedaSheetOpen, setMonedaSheetOpen] = useState(false);

  // Fase 1 cotización — bloque comercial (incoterm, días libres, validez, etc.)
  const DEFAULT_COMERCIAL: ProformaComercial = {
    incoterm: "FOB",
    puerto_carga: "Qingdao, China",
    puerto_destino: "Buenaventura, Colombia",
    transito_dias: 35,
    dias_libres_destino: 7,
    tiempo_nacionalizacion_dias: 5,
    validez_dias: 30,
    condiciones_pago: "T/T 30% adelanto, 70% contra copia BL",
    observaciones: "",
    sobrecostos_posibles: [],
  };
  const [comercial, setComercial] = useState<ProformaComercial>(DEFAULT_COMERCIAL);

  // Rediseño UX — secciones colapsables (default: cerradas)
  const [comercialOpen, setComercialOpen] = useState(false);
  
  const impactPagination = usePagination(10);

  // Autosave (Fase B-5) — guarda 30s después del último cambio si hay dirty
  const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const lastSavedSigRef = useRef<string>("");
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (current?.payload?.sim_params) {
      // Normalizar legacy (number) → SimulatedGastoOverride
      const rawEd = current.payload.sim_params.gastos_editados ?? {};
      const normEd: Record<string, SimulatedGastoOverride> = {};
      for (const [k, v] of Object.entries(rawEd)) {
        normEd[k] = typeof v === "number" ? { monto: v } : { ...v };
      }
      setGastosEditados(normEd);
      setGastosNuevos(
        (current.payload.sim_params.gastos_nuevos ?? []).map((g) => ({
          ...g,
          id: ensureUuid(g.id),
          iva_local: Number(g.iva_local ?? 0),
        })),
      );
      setItemsEditados({ ...(current.payload.sim_params.items_editados ?? {}) });
      setItemsNuevos([...(current.payload.sim_params.items_nuevos ?? [])]);
      setArancelOverrides({ ...(current.payload.sim_params.arancel_pct_overrides ?? {}) });
      setIvaOverrides({ ...(current.payload.sim_params.iva_pct_overrides ?? {}) });
      setBeneficioOverrides({ ...(current.payload.sim_params.beneficio_overrides ?? {}) });
      setComercial({ ...DEFAULT_COMERCIAL, ...(current.payload.sim_params.comercial ?? {}) });
      setTrmOverrides({ ...((current.payload.sim_params as { trm_overrides?: Record<string, number> }).trm_overrides ?? {}) });
    } else {
      setGastosEditados({});
      setGastosNuevos([]);
      setItemsEditados({});
      setItemsNuevos([]);
      setArancelOverrides({});
      setIvaOverrides({});
      setBeneficioOverrides({});
      setComercial(DEFAULT_COMERCIAL);
      setTrmOverrides({});
    }
    // Reset autosave signature al cambiar de simulación
    lastSavedSigRef.current = "__init__";
    setAutosaveStatus("idle");
  }, [current?.id]);

  // Auto-seleccionar primera simulación si no hay nada en URL
  useEffect(() => {
    if (!selectedId && simulations.length > 0) {
      setSearchParams((p) => {
        const next = new URLSearchParams(p);
        next.set("simId", simulations[0].id);
        return next;
      }, { replace: true });
    }
  }, [selectedId, simulations, setSearchParams]);

  const selectSim = (id: string) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("simId", id);
      return next;
    });
  };

  // ── Cálculos (server-side via RPC, debounced 300ms) ─────────────────────
  // ENG-01 R3: el motor TS fue reemplazado por `calculate_landed_cost_preview`.
  // Disparamos DOS queries (baseline + projected) — el debounce nativo del hook
  // protege la red durante el arrastre de sliders.
  // TRM base efectiva: si el embarque base no tiene TRM Aduana (sin DIM),
  // usamos la TRM de mercado actual como fallback para que el slider tenga efecto.
  const trmAduanaOriginal = Number(current?.payload?.cost_engine_input?.trmAduana ?? 0);
  const hasTrmAduana = trmAduanaOriginal > 0;
  const trmBaseEffective = hasTrmAduana ? trmAduanaOriginal : Number(trmMarket ?? 0);

  // Fase 2 V3 — Si el payload es V3, los ítems viven en `payload.items` (raíz).
  // Los hidratamos dentro de `cost_engine_input.items` ANTES de hidratar TRMs
  // para que el motor (que lee desde cost_engine_input) los vea correctamente.
  // En V2, `cost_engine_input.items` ya es la fuente de verdad.
  const effectiveBaseItems: EngineItemInput[] = useMemo(() => {
    const p = current?.payload as unknown;
    if (!p) return [];
    if (isPayloadV3(p)) return ((p as ProformaPayloadV3).items ?? []) as EngineItemInput[];
    return ((p as ProformaPayload).cost_engine_input?.items ?? []) as EngineItemInput[];
  }, [current?.payload]);

  const effectiveBaseGastos: EngineGastoInput[] = useMemo(() => {
    const p = current?.payload as unknown;
    if (!p) return [];
    if (isPayloadV3(p)) return ((p as ProformaPayloadV3).gastos ?? []) as unknown as EngineGastoInput[];
    return ((p as ProformaPayload).cost_engine_input?.gastos ?? []) as EngineGastoInput[];
  }, [current?.payload]);

  const hydratedCostEngineInput = useMemo(() => {
    const cei = current?.payload?.cost_engine_input;
    if (!cei) return null;
    const ceiWithItems: CostEngineInput = {
      ...cei,
      items: effectiveBaseItems,
      gastos: effectiveBaseGastos,
    };
    return hydrateFxRates(ceiWithItems, latestTrmMap, trmBaseEffective);
  }, [current?.payload?.cost_engine_input, effectiveBaseItems, effectiveBaseGastos, latestTrmMap, trmBaseEffective]);

  const baselineRawInput: LandedCostPreviewInput | null = useMemo(() => {
    if (!hydratedCostEngineInput) return null;
    // Mismo fallback que projected: si el embarque base no tiene TRM aduanera,
    // usamos la TRM de mercado para que el RPC no falle por trm_aduana=0
    // y el Análisis de Impacto Unitario tenga datos base que mostrar.
    const ceiBase: CostEngineInput =
      !hasTrmAduana && trmBaseEffective > 0
        ? { ...hydratedCostEngineInput, trmAduana: trmBaseEffective }
        : hydratedCostEngineInput;
    return costEngineInputToRaw(ceiBase);
  }, [hydratedCostEngineInput, hasTrmAduana, trmBaseEffective]);

  const projectedRawInput: LandedCostPreviewInput | null = useMemo(() => {
    const cei = hydratedCostEngineInput;
    if (!cei) return null;
    // Si no hay TRM aduanera, inyectamos la TRM de mercado como base antes
    // de aplicar el factor del slider.
    const ceiBase: CostEngineInput =
      !hasTrmAduana && trmBaseEffective > 0
        ? { ...cei, trmAduana: trmBaseEffective }
        : cei;
    const adjusted = applySimParams(
      ceiBase,
      gastosEditados,
      gastosNuevos,
      itemsEditados,
      itemsNuevos,
      arancelOverrides,
      ivaOverrides,
      beneficioOverrides,
      trmOverrides,
    );
    return costEngineInputToRaw(adjusted);
  }, [
    hydratedCostEngineInput,
    hasTrmAduana,
    trmBaseEffective,
    gastosEditados,
    gastosNuevos,
    itemsEditados,
    itemsNuevos,
    arancelOverrides,
    ivaOverrides,
    beneficioOverrides,
    trmOverrides,
  ]);

  // ── Snapshot V3 ("línea base") ──────────────────────────────────────────
  // En V3 el baseline NO se calcula automáticamente desde el embarque base.
  // Es un checkpoint explícito guardado por el usuario (payload.snapshot).
  const isV3Sim = current ? isPayloadV3(current.payload) : false;
  const v3Snapshot = useMemo(() => {
    if (!current || !isV3Sim) return null;
    return (current.payload as unknown as ProformaPayloadV3).snapshot ?? null;
  }, [current, isV3Sim]);
  const hasSnapshot = !!v3Snapshot;

  // En V3 desactivamos la query baseline (ahorra round-trip RPC).
  const baselineQuery = useLandedCostPreview(isV3Sim ? null : baselineRawInput);
  const projectedQuery = useLandedCostPreview(projectedRawInput);

  // baselineResult efectivo:
  //  - V2: resultado del RPC baseline (comportamiento original).
  //  - V3 con snapshot: snapshot.rpc_result.
  //  - V3 sin snapshot: null (no hay comparación).
  const baselineResult: LandedCostPreviewResult | null = useMemo(() => {
    if (isV3Sim) {
      return (v3Snapshot?.rpc_result as LandedCostPreviewResult | undefined) ?? null;
    }
    return baselineQuery.data ?? null;
  }, [isV3Sim, v3Snapshot, baselineQuery.data]);
  const projectedResult = projectedQuery.data ?? null;

  // Si el usuario debe ver el bloque "Landed Base / Δ" en KPI ribbon y tabla.
  const hasBaselineDisplay = !isV3Sim || hasSnapshot;

  // Suma de landed_total_cop por ítem = total del escenario.
  const sumLanded = (r: LandedCostPreviewResult | null) =>
    (r?.items ?? []).reduce((acc, it) => acc + Number(it.landed_total_cop ?? 0), 0);

  const baselineTotal = sumLanded(baselineResult);
  const projectedTotal = sumLanded(projectedResult);
  // IVA proyectado (recuperable). NO se capitaliza al landed; se muestra para flujo de caja.
  const projectedIvaTotal = (projectedResult?.items ?? []).reduce(
    (acc, it) => acc + Number(it.iva_cop ?? 0),
    0,
  );
  const projectedDesembolso = projectedTotal + projectedIvaTotal;
  const deltaAbs = projectedTotal - baselineTotal;
  const deltaPct = baselineTotal > 0 ? (deltaAbs / baselineTotal) * 100 : 0;
  const deltaIsBad = deltaAbs > 0;
  const deltaIsZero = Math.abs(deltaAbs) < 0.5;

  const isCalculating = baselineQuery.isFetching || projectedQuery.isFetching;
  const calcError = (projectedQuery.error ?? baselineQuery.error) as Error | null;
  const [dismissedErrorMsg, setDismissedErrorMsg] = useState<string | null>(null);
  const calcErrorVisible = calcError && calcError.message !== dismissedErrorMsg;


  // Fase 2 V3 — usar effectiveBaseItems/effectiveBaseGastos para que en V3
  // (donde cost_engine_input.items/gastos están vacíos) los conteos reflejen
  // la fuente de verdad real (payload.items/payload.gastos en raíz).
  const baseItemsCount = effectiveBaseItems.length;
  const itemsEditadosCount = Object.keys(itemsEditados).length;
  const itemsNuevosCount = itemsNuevos.length;
  const itemsCount = baseItemsCount + itemsNuevosCount;
  const baseGastosCount = effectiveBaseGastos.length;
  const editedCount = Object.keys(gastosEditados).length;
  const nuevosCount = gastosNuevos.length;
  const gastosCount = baseGastosCount + nuevosCount;
  // Compliance overrides count (incluye base + nuevos)
  const complianceOverridesBase =
    Object.keys(arancelOverrides).filter((id) => effectiveBaseItems.some((i) => i.id === id)).length +
    Object.keys(ivaOverrides).filter((id) => effectiveBaseItems.some((i) => i.id === id)).length;
  const itemDirtyCount = itemsEditadosCount + complianceOverridesBase;

  // ── Auto-escalada: detecta si el snapshot exige modo Avanzado ─────────────
  const forceAdvanced = useMemo(() => {
    const items = effectiveBaseItems;
    const gastos = effectiveBaseGastos;
    const itemsForCheck = [
      ...items.map((it) => ({
        tarifa_ico: (it as { tarifa_ico?: number | null }).tarifa_ico ?? null,
        grados_alcoholicos: (it as { grados_alcoholicos?: number | null }).grados_alcoholicos ?? null,
        beneficio_aduanero: (it as { beneficio_aduanero?: string | null }).beneficio_aduanero ?? null,
        aplica_tlc: (it as { aplica_tlc?: boolean | null }).aplica_tlc ?? null,
      })),
      ...itemsNuevos.map((it) => ({
        tarifa_ico: it.tarifa_ico ?? null,
        grados_alcoholicos: it.grados_alcoholicos ?? null,
        beneficio_aduanero: it.beneficio_aduanero ?? null,
        aplica_tlc: it.aplica_tlc ?? null,
      })),
    ];
    return shouldForceAdvanced(itemsForCheck, gastos);
  }, [effectiveBaseItems, effectiveBaseGastos, itemsNuevos]);

  // ── Baseline context: TRM original y pool de flete original (Misión 2A) ──
  // (trmBaseOriginal/Effective ya calculados arriba para uso en projectedRawInput y UI)

  const gastosList = effectiveBaseGastos;

  // ── Análisis unitario (Misión 2C): join baseline + projected por itemId ──
  type UnitRow = {
    itemId: string;
    sku: string;
    descripcion: string;
    cantidad: number;
    fobUnitario: number;
    landedBaseUnit: number | null;
    landedProjUnit: number | null;
    deltaUnit: number | null;
    hasCalculation: boolean;
    /** Fase 3 — estado vs línea base: "new" (no estaba en snapshot), "removed" (estaba pero ya no), "match" (ambos). */
    snapshotStatus?: "new" | "removed" | "match";
  };
  // Mapa gastoId → concepto (para tooltip de desglose por ítem)
  const gastoConceptoById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of effectiveBaseGastos) if (g.id) m.set(g.id, g.concepto ?? "Gasto");
    for (const g of gastosNuevos) m.set(g.id, g.concepto ?? "Gasto");
    return m;
  }, [effectiveBaseGastos, gastosNuevos]);

  // Agrupar allocations del RPC por itemId → [{ gastoId, concepto, montoCOP, baseDeclarada }]
  const allocationsByItem = useMemo(() => {
    const map = new Map<string, Array<{ gastoId: string | null; concepto: string; montoCOP: number; baseDeclarada: string }>>();
    const allocs = projectedResult?.allocations ?? [];
    for (const a of allocs) {
      const arr = map.get(a.item_id) ?? [];
      arr.push({
        gastoId: a.gasto_id,
        concepto: a.gasto_id ? (gastoConceptoById.get(a.gasto_id) ?? "Gasto") : "Gasto",
        montoCOP: Number(a.monto_cop ?? 0),
        baseDeclarada: a.base_declarada,
      });
      map.set(a.item_id, arr);
    }
    return map;
  }, [projectedResult?.allocations, gastoConceptoById]);

  const unitRows: UnitRow[] = useMemo(() => {
    const baseItemsMeta = effectiveBaseItems;
    // Combinar metadatos: items base (con overrides aplicados) + nuevos
    const itemsMeta: EngineItemInput[] = [
      ...baseItemsMeta.map((it) => {
        const ov = itemsEditados[it.id];
        if (!ov) return it;
        return {
          ...it,
          cantidad: ov.cantidad ?? it.cantidad,
          precio_unitario: ov.precio_unitario ?? it.precio_unitario,
        };
      }),
      ...itemsNuevos.map((it) => ({
        id: it.id,
        documento_id: it.documento_id,
        sku: it.sku ?? null,
        descripcion: it.descripcion ?? null,
        cantidad: it.cantidad,
        precio_unitario: it.precio_unitario,
        sub_total: it.sub_total,
        peso_bruto_kg: it.peso_bruto_kg ?? 0,
      })),
    ];
    const baseMap = new Map((baselineResult?.items ?? []).map((r) => [r.id, r]));
    const projectedMap = new Map((projectedResult?.items ?? []).map((r) => [r.id, r]));
    const rows: UnitRow[] = itemsMeta.map((meta) => {
      const proj = projectedMap.get(meta.id);
      const baseItem = baseMap.get(meta.id);
      const cantidad = Number(meta?.cantidad ?? 0);
      const fobUnit = Number(meta?.precio_unitario ?? 0);
      const landedBaseUnit = baseItem ? Number(baseItem.landed_unit_cop ?? 0) : null;
      const landedProjUnit = proj ? Number(proj.landed_unit_cop ?? 0) : null;
      const deltaUnit = landedProjUnit != null && landedBaseUnit != null ? landedProjUnit - landedBaseUnit : null;
      // Fase 3 — estado solo si hay snapshot (V3); en V2 no marcamos status.
      let snapshotStatus: UnitRow["snapshotStatus"] = undefined;
      if (hasSnapshot) {
        snapshotStatus = baseItem ? "match" : "new";
      }
      return {
        itemId: meta.id,
        sku: meta?.sku ?? "—",
        descripcion: meta?.descripcion ?? "(sin descripción)",
        cantidad,
        fobUnitario: fobUnit,
        landedBaseUnit,
        landedProjUnit,
        deltaUnit,
        hasCalculation: !!proj,
        snapshotStatus,
      };
    });
    // Fase 3 — ítems que estaban en el snapshot pero ya no están en projected.
    if (hasSnapshot) {
      const projectedIds = new Set(rows.map((r) => r.itemId));
      for (const baseItem of baselineResult?.items ?? []) {
        if (projectedIds.has(baseItem.id)) continue;
        rows.push({
          itemId: baseItem.id,
          sku: "—",
          descripcion: "(eliminado del escenario)",
          cantidad: 0,
          fobUnitario: 0,
          landedBaseUnit: Number(baseItem.landed_unit_cop ?? 0),
          landedProjUnit: null,
          deltaUnit: null,
          hasCalculation: false,
          snapshotStatus: "removed",
        });
      }
    }
    return rows;
  }, [baselineResult, projectedResult, effectiveBaseItems, itemsEditados, itemsNuevos, hasSnapshot]);

  const fmtMoney = (val: number, currency: string) => {
    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: currency.toUpperCase(),
        maximumFractionDigits: currency.toUpperCase() === "COP" ? 0 : 2,
      }).format(val);
    } catch {
      return `${val.toLocaleString("es-CO")} ${currency}`;
    }
  };

  // Sim params actuales (memo) — base para autosave + handleSave
  const currentSimParams = useMemo(() => ({
    gastos_editados: gastosEditados,
    gastos_nuevos: gastosNuevos,
    items_editados: itemsEditados,
    items_nuevos: itemsNuevos,
    arancel_pct_overrides: arancelOverrides,
    iva_pct_overrides: ivaOverrides,
    beneficio_overrides: beneficioOverrides,
    comercial,
    trm_overrides: trmOverrides,
  }), [gastosEditados, gastosNuevos, itemsEditados, itemsNuevos, arancelOverrides, ivaOverrides, beneficioOverrides, comercial, trmOverrides]);

  const currentSig = useMemo(() => JSON.stringify(currentSimParams), [currentSimParams]);

  const persistPayload = async (silent: boolean) => {
    if (!current) return;
    const isV3 = isPayloadV3(current.payload);
    let newPayload: ProformaPayload;

    if (isV3) {
      // Fase 2 V3 — fold itemsEditados + itemsNuevos en payload.items.
      // Lo mismo para gastos. Mantenemos sim_params slim (comercial + TRM overrides).
      // BUG 3 fix — Dedup itemsNuevos contra effectiveBaseItems por id antes
      // del fold. El optimistic cache update del save anterior ya pudo haber
      // escrito el ítem/gasto en payload mientras el staging aún lo tenía;
      // sin este filtro applySimParams concatenaría duplicados.
      const baseIds = new Set(effectiveBaseItems.map((it) => it.id));
      const itemsNuevosDedup = itemsNuevos.filter((it) => !baseIds.has(it.id));
      const baseGastoIds = new Set(effectiveBaseGastos.map((g) => g.id).filter(Boolean));
      const gastosNuevosDedup = gastosNuevos.filter((g) => !baseGastoIds.has(g.id));
      const adjusted = applySimParams(
        hydratedCostEngineInput ?? (current.payload.cost_engine_input as CostEngineInput),
        gastosEditados,
        gastosNuevosDedup,
        itemsEditados,
        itemsNuevosDedup,
        arancelOverrides,
        ivaOverrides,
        beneficioOverrides,
        {}, // trmOverrides: ephemeral, no se foldean en items/gastos
      );
      const v3Payload = current.payload as unknown as ProformaPayloadV3;
      newPayload = {
        ...v3Payload,
        items: adjusted.items as EngineItemInput[],
        gastos: adjusted.gastos as unknown as ProformaPayloadV3["gastos"],
        cost_engine_input: {
          ...v3Payload.cost_engine_input,
          // En V3 cost_engine_input.items/gastos se mantienen vacíos
          items: [],
          gastos: [],
          compliance: {
            arancelPctByItemId: { ...adjusted.compliance.arancelPctByItemId },
            ivaPctByItemId: { ...adjusted.compliance.ivaPctByItemId },
          },
        },
        sim_params: {
          comercial,
          trm_overrides: trmOverrides,
        },
      } as unknown as ProformaPayload;
    } else {
      newPayload = {
        ...current.payload,
        sim_params: currentSimParams,
      };
    }

    try {
      if (silent) setAutosaveStatus("saving");
      await updateMut.mutateAsync({ id: current.id, payload: newPayload });
      lastSavedSigRef.current = currentSig;
      if (silent) {
        setAutosaveStatus("saved");
        if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
        savedFlashRef.current = setTimeout(() => setAutosaveStatus("idle"), 2500);
      } else {
        toast({ title: "Simulación guardada", description: "Parámetros actualizados." });
        setAutosaveStatus("saved");
        if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
        savedFlashRef.current = setTimeout(() => setAutosaveStatus("idle"), 2500);
      }
      // Tras fold V3, los overrides ephemeral ya están persistidos como base —
      // limpiamos el estado en memoria para evitar doble aplicación al re-render.
      if (isV3) {
        // Fase 2 hotfix — escribir el payload nuevo en la cache ANTES de limpiar
        // los staging slots. Sin esto, hay una ventana entre `setItemsNuevos([])`
        // y la llegada del refetch en la que `current.payload.items` aún está
        // vacío y los ítems "desaparecen" de la UI hasta que el invalidate resuelve.
        queryClient.setQueryData(
          ["proforma-simulation", current.id],
          (old: ProformaSimulation | null | undefined) =>
            old ? { ...old, payload: newPayload } : old,
        );
        setItemsEditados({});
        setItemsNuevos([]);
        setArancelOverrides({});
        setIvaOverrides({});
        setBeneficioOverrides({});
        setGastosEditados({});
        setGastosNuevos([]);
        // Marcar como "guardado" tras la limpieza para que el efecto de
        // autosave no dispare un timer fantasma con itemsNuevos=[].
        // currentSig se recalcula en el próximo render con los slots vacíos;
        // lo sincronizamos en el siguiente tick.
        queueMicrotask(() => {
          lastSavedSigRef.current = JSON.stringify({
            gastos_editados: {},
            gastos_nuevos: [],
            items_editados: {},
            items_nuevos: [],
            arancel_pct_overrides: {},
            iva_pct_overrides: {},
            beneficio_overrides: {},
            comercial,
            trm_overrides: trmOverrides,
          });
        });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      setAutosaveStatus("idle");
      if (!silent) toast({ title: "Error al guardar", description: msg, variant: "destructive" });
    }
  };

  const handleSave = () => persistPayload(false);

  // ── Baseline (snapshot) handlers ────────────────────────────────────────
  // Estado para confirmaciones de actualizar/eliminar línea base.
  const [baselineConfirm, setBaselineConfirm] = useState<null | "update" | "delete">(null);
  const [baselineBusy, setBaselineBusy] = useState(false);

  // Validación: hay al menos un ítem con cantidad > 0 y FOB > 0.
  const canSaveBaseline = useMemo(() => {
    if (!isV3Sim) return false;
    return effectiveBaseItems.some(
      (it) => Number(it.cantidad ?? 0) > 0 && Number(it.precio_unitario ?? 0) > 0,
    );
  }, [isV3Sim, effectiveBaseItems]);

  const writeSnapshot = async () => {
    if (!current || !isV3Sim) return;
    if (!projectedResult || !hydratedCostEngineInput) {
      toast({
        title: "Aún no hay cálculo proyectado",
        description: "Espera a que termine el motor de costeo y vuelve a intentarlo.",
        variant: "destructive",
      });
      return;
    }
    setBaselineBusy(true);
    try {
      const v3Payload = current.payload as unknown as ProformaPayloadV3;
      const newPayload: ProformaPayloadV3 = {
        ...v3Payload,
        snapshot: {
          cost_engine_input: hydratedCostEngineInput,
          rpc_result: projectedResult,
          saved_at: new Date().toISOString(),
        },
      };
      await updateMut.mutateAsync({
        id: current.id,
        payload: newPayload as unknown as ProformaPayload,
      });
      queryClient.setQueryData(
        ["proforma-simulation", current.id],
        (old: ProformaSimulation | null | undefined) =>
          old ? { ...old, payload: newPayload as unknown as ProformaPayload } : old,
      );
      toast({ title: "Línea base guardada" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo guardar la línea base", description: msg, variant: "destructive" });
    } finally {
      setBaselineBusy(false);
      setBaselineConfirm(null);
    }
  };

  /**
   * Elimina un gasto del escenario.
   * - V3: filtra `payload.gastos` y persiste inmediatamente.
   * - V2: solo inhabilita (aplica_costeo_inventario = false) vía override.
   *   No se eliminan gastos en V2 para preservar el snapshot inmutable.
   */
  const removeGasto = async (id: string) => {
    if (!current) return;
    const isV3 = isPayloadV3(current.payload);
    if (isV3) {
      const v3Payload = current.payload as unknown as ProformaPayloadV3;
      const newPayload: ProformaPayloadV3 = {
        ...v3Payload,
        gastos: (v3Payload.gastos ?? []).filter((g) => g.id !== id),
      };
      try {
        await updateMut.mutateAsync({
          id: current.id,
          payload: newPayload as unknown as ProformaPayload,
        });
        queryClient.setQueryData(
          ["proforma-simulation", current.id],
          (old: ProformaSimulation | null | undefined) =>
            old ? { ...old, payload: newPayload as unknown as ProformaPayload } : old,
        );
        toast({ title: "Gasto eliminado" });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Error desconocido";
        toast({ title: "No se pudo eliminar el gasto", description: msg, variant: "destructive" });
      }
    } else {
      // V2: inhabilitar (no eliminar) para preservar snapshot
      setGastosEditados((prev) => ({
        ...prev,
        [id]: { ...(prev[id] ?? {}), aplica_costeo_inventario: false },
      }));
    }
  };

  const handleDeleteBaseline = async () => {
    if (!current || !isV3Sim) return;
    setBaselineBusy(true);
    try {
      const v3Payload = current.payload as unknown as ProformaPayloadV3;
      const newPayload: ProformaPayloadV3 = { ...v3Payload };
      delete (newPayload as { snapshot?: unknown }).snapshot;
      await updateMut.mutateAsync({
        id: current.id,
        payload: newPayload as unknown as ProformaPayload,
      });
      queryClient.setQueryData(
        ["proforma-simulation", current.id],
        (old: ProformaSimulation | null | undefined) =>
          old ? { ...old, payload: newPayload as unknown as ProformaPayload } : old,
      );
      toast({ title: "Línea base eliminada" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo eliminar la línea base", description: msg, variant: "destructive" });
    } finally {
      setBaselineBusy(false);
      setBaselineConfirm(null);
    }
  };

  // Autosave: 30s después del último cambio si la firma difiere de lo guardado
  useEffect(() => {
    if (!current) return;
    if (lastSavedSigRef.current === "__init__") {
      // primera hidratación tras cambio de sim
      lastSavedSigRef.current = currentSig;
      return;
    }
    if (lastSavedSigRef.current === currentSig) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      persistPayload(true);
    }, 30_000);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSig, current?.id]);

  const isDirty = lastSavedSigRef.current !== currentSig && lastSavedSigRef.current !== "__init__";

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMut.mutateAsync(deleteId);
      toast({ title: "Simulación eliminada" });
      if (selectedId === deleteId) {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete("simId");
          return next;
        });
      }
      setDeleteId(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo eliminar", description: msg, variant: "destructive" });
    }
  };

  // ── Validación pre-export (no bloqueante) ─────────────────────────────────
  const validateExport = (): ExportWarning[] => {
    const warnings: ExportWarning[] = [];
    const allItems = [...effectiveBaseItems, ...itemsNuevos];

    // Regla 1 — HS Code duplicado en ítems con descripciones distintas
    const hsGroups = new Map<string, Array<{ sku: string; descripcion: string }>>();
    allItems.forEach((it) => {
      const ov = itemsEditados[(it as { id: string }).id];
      const hsRaw = (((ov as { hs_code?: string | null } | undefined)?.hs_code
        ?? (it as { hs_code?: string | null }).hs_code) ?? "").toString().trim();
      if (!hsRaw) return;
      const sku = ((it as { sku?: string | null }).sku ?? "").toString().trim() || "—";
      const descripcion = ((it as { descripcion?: string | null }).descripcion ?? "").toString().trim();
      const arr = hsGroups.get(hsRaw) ?? [];
      arr.push({ sku, descripcion });
      hsGroups.set(hsRaw, arr);
    });
    hsGroups.forEach((items, hs) => {
      if (items.length < 2) return;
      const firstThree = (s: string) =>
        s.toLowerCase().split(/\s+/).filter(Boolean).slice(0, 3).join(" ");
      const baseline = firstThree(items[0].descripcion);
      const diferentes = items.some((x) => firstThree(x.descripcion) !== baseline);
      if (diferentes) {
        warnings.push({
          code: "W_HS_DUPLICATED",
          message: `${items.length} ítems con descripciones diferentes comparten el HS Code ${hs}. Verificar clasificación.`,
          details: items.map((x) => `${x.sku} — ${x.descripcion || "(sin descripción)"}`),
        });
      }
    });

    // Regla 2 — Gastos activos con monto cero
    const gastosActivosCero: string[] = [];
    effectiveBaseGastos.forEach((g) => {
      const ov = gastosEditados[(g as { id?: string | null }).id ?? ""];
      const aplica = ov?.aplica_costeo_inventario ?? (g as { aplica_costeo_inventario?: boolean }).aplica_costeo_inventario ?? true;
      const monto = Number(ov?.monto ?? (g as { monto?: number | null }).monto ?? 0);
      if (aplica && monto === 0) {
        gastosActivosCero.push(((g as { concepto?: string | null }).concepto ?? "—").toString());
      }
    });
    gastosNuevos.forEach((g) => {
      if ((g.aplica_costeo_inventario ?? true) && Number(g.monto ?? 0) === 0) {
        gastosActivosCero.push(g.concepto || "—");
      }
    });
    if (gastosActivosCero.length > 0) {
      warnings.push({
        code: "W_GASTO_ZERO",
        message: `Hay ${gastosActivosCero.length} gastos activos sin valor asignado. El cálculo puede estar incompleto.`,
        details: gastosActivosCero,
      });
    }

    // Regla 3 — Ítems sin peso/volumen cuando hay gastos físicos (Peso/CBM)
    const gastosFisicos = [
      ...effectiveBaseGastos.filter((g) => {
        const ov = gastosEditados[(g as { id?: string | null }).id ?? ""];
        const base = (ov?.base_prorrateo ?? (g as { base_prorrateo?: string }).base_prorrateo ?? "").toString();
        return base === "Peso" || base === "CBM";
      }),
      ...gastosNuevos.filter((g) => g.base_prorrateo === "Peso" || (g.base_prorrateo as string) === "CBM"),
    ];
    if (gastosFisicos.length > 0) {
      const sinFisica: string[] = [];
      allItems.forEach((it) => {
        const ov = itemsEditados[(it as { id: string }).id];
        const peso = Number(ov?.peso_bruto_kg ?? (it as { peso_bruto_kg?: number | null }).peso_bruto_kg ?? 0);
        const vol = Number(ov?.volumen_cbm ?? (it as { volumen_cbm?: number | null }).volumen_cbm ?? 0);
        if (peso === 0 && vol === 0) {
          const sku = ((it as { sku?: string | null }).sku ?? "").toString().trim() || "(sin SKU)";
          const desc = ((it as { descripcion?: string | null }).descripcion ?? "").toString().trim();
          sinFisica.push(desc ? `${sku} — ${desc}` : sku);
        }
      });
      if (sinFisica.length > 0) {
        warnings.push({
          code: "W_NO_WEIGHT",
          message: `Hay ${sinFisica.length} ítems sin peso ni volumen declarado. La distribución de gastos físicos usará el valor FOB como fallback, lo que puede ser inexacto.`,
          details: sinFisica,
        });
      }
    }

    // Regla 4 — SKU vacío o "N/A"
    const sinSku: string[] = [];
    allItems.forEach((it, idx) => {
      const sku = ((it as { sku?: string | null }).sku ?? "").toString().trim();
      if (!sku || sku.toUpperCase() === "N/A") {
        const desc = ((it as { descripcion?: string | null }).descripcion ?? "").toString().trim();
        sinSku.push(desc || `Ítem #${idx + 1}`);
      }
    });
    if (sinSku.length > 0) {
      warnings.push({
        code: "W_NO_SKU",
        message: `Hay ${sinSku.length} ítems sin SKU definido. La cotización mostrará 'N/A'.`,
        details: sinSku,
      });
    }

    return warnings;
  };

  const onExportClick = () => {
    if (!current || !projectedResult) {
      void handleExport();
      return;
    }
    const warnings = validateExport();
    if (warnings.length === 0) {
      void handleExport();
    } else {
      setExportWarnings(warnings);
    }
  };

  // ── Exportar a Excel (mismo formato que Nexo Comercial) ───────────────────
  const handleExport = async () => {
    if (!current) return;
    if (!projectedResult) {
      toast({
        title: "Aún no hay cálculo proyectado",
        description: "Espera a que termine el motor de costeo y vuelve a intentarlo.",
        variant: "destructive",
      });
      return;
    }
    setExportBusy(true);
    try {
      const cei = current.payload?.cost_engine_input;
      // Fase 2 V3 — usar effectiveBaseItems para que en V3 el export incluya
      // los ítems del payload raíz (cost_engine_input.items está vacío en V3).
      const baseItemsMeta = effectiveBaseItems;
      const itemsMetaById = new Map(baseItemsMeta.map((it) => [it.id, it]));
      const newItemsById = new Map(itemsNuevos.map((it) => [it.id, it]));

      // Fase 3 — Mapa de costo unitario en línea base (snapshot V3) para columnas comparativas.
      const baselineUnitMap = new Map(
        (baselineResult?.items ?? []).map((b) => [b.id, Number(b.landed_unit_cop ?? 0)]),
      );

      const rows: ProformaRow[] = projectedResult.items.map((proj) => {
        const meta =
          itemsMetaById.get(proj.id) ?? newItemsById.get(proj.id) ?? null;
        const ov = itemsEditados[proj.id];
        const cantidad = Number(ov?.cantidad ?? meta?.cantidad ?? 0);
        const benef =
          beneficioOverrides[proj.id] ??
          meta?.beneficio_aduanero ??
          (meta?.aplica_tlc ? "TLC_PURO" : "GENERAL");
        const m = meta as {
          sku?: string | null;
          descripcion?: string | null;
          hs_code?: string | null;
          unidad_medida?: string | null;
          unidad?: string | null;
          peso_bruto_kg?: number | null;
          volumen_cbm?: number | null;
        } | null;
        return {
          sku: m?.sku ?? null,
          descripcion: m?.descripcion ?? null,
          cantidad,
          hs_code: (((ov as { hs_code?: string | null } | undefined)?.hs_code ?? m?.hs_code) ?? "").toString().trim() || null,
          costo_unitario_cop: Number(proj.landed_unit_cop ?? 0),
          beneficio_aduanero: benef,
          unidad: ov?.unidad_medida ?? m?.unidad ?? m?.unidad_medida ?? "UN",
          peso_bruto_kg: Number(ov?.peso_bruto_kg ?? m?.peso_bruto_kg ?? 0),
          volumen_cbm: Number(ov?.volumen_cbm ?? m?.volumen_cbm ?? 0),
          arancel_pct: Number(proj.arancel_pct ?? 0),
          iva_pct: Number(proj.iva_pct ?? 0),
          // RPC ya devuelve ICO total por línea (grados × tarifa × litros × cantidad).
          // No multiplicar por cantidad otra vez.
          ico_cop: Number((proj as { ico_cop?: number | null }).ico_cop ?? 0),
          costo_base_unitario_cop: hasSnapshot ? (baselineUnitMap.get(proj.id) ?? null) : null,
        };
      });

      // Construir desglose de gastos para Sheet 4 (scope global, no por incoterm).
      const presetByConcept = new Map(
        PRESETS_BY_INCOTERM[comercial.incoterm].map((p) => [p.concepto, p]),
      );
      const baseGastos = effectiveBaseGastos;
      const desgloseRows = [
        ...baseGastos.map((g) => {
          const matched = presetByConcept.get(g.concepto ?? "");
          return {
            concepto: g.concepto ?? "—",
            scope: inferScope(g.concepto),
            moneda: String(g.moneda ?? "COP"),
            monto: Number(gastosEditados[g.id ?? ""]?.monto ?? g.monto ?? 0),
            base_prorrateo: String(g.base_prorrateo ?? "Valor"),
            incluir_en_base_gravable: Boolean((g as { incluir_en_base_gravable?: boolean }).incluir_en_base_gravable),
          };
        }),
        ...gastosNuevos.map((g) => {
          const matched = presetByConcept.get(g.concepto);
          return {
            concepto: g.concepto,
            scope: inferScope(g.concepto),
            moneda: g.moneda,
            monto: Number(g.monto ?? 0),
            base_prorrateo: String(g.base_prorrateo),
            incluir_en_base_gravable: Boolean(matched?.incluir_en_base_gravable),
          };
        }),
      ];

      await exportProformaToExcel(rows, {
        nombre: current.nombre,
        proveedor: current.payload?.embarque_base?.proveedor ?? null,
        numeroDoBase: current.payload?.embarque_base?.numero_do ?? null,
        trmUsada: projectedResult.trm_aduana_usada ?? null,
        trmSource: projectedResult.trm_source ?? null,
        incoterm: comercial.incoterm,
        puertoCarga: comercial.puerto_carga,
        puertoDestino: comercial.puerto_destino,
        transitoDias: comercial.transito_dias,
        validezDias: comercial.validez_dias,
        condicionesPago: comercial.condiciones_pago,
        diasLibresDestino: comercial.dias_libres_destino,
        tiempoNacionalizacionDias: comercial.tiempo_nacionalizacion_dias,
        observaciones: comercial.observaciones,
        sobrecostosPosibles: comercial.sobrecostos_posibles,
        gastos: desgloseRows,
        trmDate: trmAduanaDate,
        trmOutdated: trmIsOutdated,
        ivaTotal: projectedIvaTotal,
        desembolsoTotal: projectedDesembolso,
      });
      toast({ title: "Excel generado", description: "El archivo se está descargando." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo exportar", description: msg, variant: "destructive" });
    } finally {
      setExportBusy(false);
    }
  };

  // Derivar status visual: materialized si tiene embarque_id en payload, saved si tiene nombre custom, draft otro caso
  const deriveStatus = (s: ProformaSimulation): "draft" | "saved" | "materialized" => {
    const payload = s.payload as ProformaPayload | undefined;
    const hasEmbarque = !!(payload?.sim_params as Record<string, unknown> | undefined)?.materialized_embarque_id
      || s.status === "materialized";
    if (hasEmbarque) return "materialized";
    if (s.nombre && !/^Cotización\s|^Simulación\s/i.test(s.nombre.trim())) return "saved";
    return "draft";
  };

  const STATUS_STYLES: Record<"draft" | "saved" | "materialized", { dot: string; label: string; badge: string }> = {
    draft:        { dot: "bg-amber-500",  label: "Borrador",     badge: "border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10" },
    saved:        { dot: "bg-emerald-500",label: "Guardada",     badge: "border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10" },
    materialized: { dot: "bg-sky-500",    label: "Materializada",badge: "border-sky-500/40 text-sky-700 dark:text-sky-400 bg-sky-500/10" },
  };

  const filteredSimulations = useMemo(() => {
    const q = simSearch.trim().toLowerCase();
    let arr = simulations.map((s) => ({ ...s, _status: deriveStatus(s) }));
    if (q) arr = arr.filter((s) => (s.nombre ?? "").toLowerCase().includes(q));
    if (simStatusFilter !== "all") arr = arr.filter((s) => s._status === simStatusFilter);
    arr.sort((a, b) => {
      switch (simSort) {
        case "name_asc":  return (a.nombre ?? "").localeCompare(b.nombre ?? "");
        case "name_desc": return (b.nombre ?? "").localeCompare(a.nombre ?? "");
        case "updated_asc":  return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        case "updated_desc":
        default: return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
    });
    return arr;
  }, [simulations, simSearch, simStatusFilter, simSort]);

  const deleteTarget = simulations.find((s) => s.id === deleteId);

  // Contenido panel izquierdo (lista de simulaciones)
  const leftPane = (
    <Card className="h-full flex flex-col md:max-h-[calc(100vh-7rem)]">
          <CardHeader className="pb-3 space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Simulaciones
                <span className="text-[11px] font-normal text-muted-foreground tabular-nums">
                  ({filteredSimulations.length}/{simulations.length})
                </span>
              </CardTitle>
              <Button size="sm" onClick={() => setCloneOpen(true)} className="h-8 gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Nueva
              </Button>
            </div>
            <CardDescription className="text-xs">
              Modela escenarios sin afectar la operación.
            </CardDescription>
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={simSearch}
                onChange={(e) => setSimSearch(e.target.value)}
                placeholder="Buscar por nombre…"
                className="h-8 pl-7 text-[12px]"
              />
            </div>
            {/* Filtros */}
            <div className="flex gap-1.5">
              <Select value={simStatusFilter} onValueChange={(v) => setSimStatusFilter(v as typeof simStatusFilter)}>
                <SelectTrigger className="h-8 text-[12px] flex-1">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-[12px]">Todos los estados</SelectItem>
                  <SelectItem value="draft" className="text-[12px]">● Borrador</SelectItem>
                  <SelectItem value="saved" className="text-[12px]">● Guardada</SelectItem>
                  <SelectItem value="materialized" className="text-[12px]">● Materializada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={simSort} onValueChange={(v) => setSimSort(v as typeof simSort)}>
                <SelectTrigger className="h-8 text-[12px] w-[44px] px-2" aria-label="Ordenar">
                  <ArrowUpDown className="h-3.5 w-3.5" />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="updated_desc" className="text-[12px]">Más recientes</SelectItem>
                  <SelectItem value="updated_asc" className="text-[12px]">Más antiguas</SelectItem>
                  <SelectItem value="name_asc" className="text-[12px]">Nombre A→Z</SelectItem>
                  <SelectItem value="name_desc" className="text-[12px]">Nombre Z→A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <Separator />
          <ScrollArea className="flex-1">
            <CardContent className="p-2">
              {simsLoading ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  Cargando...
                </div>
              ) : simulations.length === 0 ? (
                <div className="p-6 text-center space-y-2">
                  <p className="text-xs text-muted-foreground">
                    No hay simulaciones aún.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCloneOpen(true)}
                    className="gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Clonar embarque
                  </Button>
                </div>
              ) : filteredSimulations.length === 0 ? (
                <div className="p-6 text-center space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Ninguna simulación coincide con los filtros.
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[11px] h-7"
                    onClick={() => { setSimSearch(""); setSimStatusFilter("all"); }}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              ) : (
                <ul className="space-y-1">
                  {filteredSimulations.map((s) => {
                    const isActive = s.id === selectedId;
                    const st = STATUS_STYLES[s._status];
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => selectSim(s.id)}
                          className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors group relative ${
                            isActive
                              ? "bg-primary/15 text-primary font-medium ring-1 ring-primary/20"
                              : "hover:bg-accent"
                          }`}
                        >
                          {/* Status dot indicator on left edge */}
                          <span
                            aria-hidden
                            className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-sm ${st.dot}`}
                          />
                          <div className="flex items-center justify-between gap-2 pl-1.5">
                            <span className="truncate flex-1">{s.nombre}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(s.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-opacity"
                              aria-label="Eliminar"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 pl-1.5">
                            <Badge variant="outline" className={`h-4 px-1.5 text-[10px] ${st.badge}`}>
                              {st.label}
                            </Badge>
                            <span>{new Date(s.updated_at).toLocaleDateString("es-CO")}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
  );

  // Contenido panel derecho (detalle del escenario)
  const rightPane = (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4 min-w-0 h-full overflow-auto pr-1">
        {/* ── Cabecera ── */}
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Simulador Pro-Forma
          </h2>
        </div>

        {!current ? (
            <Card>
              <CardContent className="py-16 text-center space-y-3">
                <TrendingUp className="h-10 w-10 text-muted-foreground/50 mx-auto" />
                <div>
                  <p className="text-sm font-medium">Selecciona una simulación</p>
                  <p className="text-xs text-muted-foreground">
                    O clona un embarque para empezar.
                  </p>
                </div>
                <Button onClick={() => setCloneOpen(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Nueva simulación
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* ── KPI Ribbon sticky (Rediseño UX) ──────────────────────── */}
              <div className="sticky top-0 z-20 -mx-1 px-1 pt-1 pb-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b">
                <div className="rounded-md border bg-muted/30 shadow-sm py-3 px-4 space-y-1.5">
                  {/* Fila 1: Landed Proyectado + Δ + acciones línea base */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-baseline gap-3 min-w-0">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Landed Proyectado</span>
                      <span className="text-2xl md:text-3xl font-bold tabular-nums tracking-tight">
                        {fmtCOP.format(projectedTotal)}
                      </span>
                      {hasBaselineDisplay && (
                        <Badge
                          variant="outline"
                          className={cn(
                            "h-6 gap-1 text-[11px] tabular-nums",
                            deltaIsZero ? "text-muted-foreground" :
                            deltaIsBad ? "border-red-500/40 text-red-600 dark:text-red-400 bg-red-500/5" :
                            "border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/5",
                          )}
                        >
                          {!deltaIsZero && (deltaIsBad ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />)}
                          Δ {deltaIsZero ? "—" : `${deltaAbs >= 0 ? "+" : ""}${fmtCOP.format(deltaAbs)} (${fmtPct(deltaPct)})`}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                      {isCalculating && (
                        <Badge variant="outline" className="h-6 gap-1 text-[10px]">
                          <Loader2 className="h-3 w-3 animate-spin" /> Calculando…
                        </Badge>
                      )}
                      {autosaveStatus === "saving" && (
                        <Badge variant="outline" className="h-6 gap-1 text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10">
                          <Loader2 className="h-3 w-3 animate-spin" /> Guardando…
                        </Badge>
                      )}
                      {autosaveStatus === "saved" && (
                        <Badge variant="outline" className="h-6 gap-1 text-[10px] border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10">
                          <Check className="h-3 w-3" /> Guardado
                        </Badge>
                      )}
                      {autosaveStatus === "idle" && isDirty && (
                        <Badge variant="outline" className="h-6 text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10">
                          Cambios sin guardar
                        </Badge>
                      )}
                      {isV3Sim && !hasSnapshot && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5 text-[11px]"
                          onClick={writeSnapshot}
                          disabled={!canSaveBaseline || !projectedResult || baselineBusy}
                        >
                          {baselineBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Snowflake className="h-3 w-3" />}
                          Guardar línea base
                        </Button>
                      )}
                      {isV3Sim && hasSnapshot && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 text-[11px]"
                            onClick={() => setBaselineConfirm("update")}
                            disabled={baselineBusy || !projectedResult}
                          >
                            <RefreshCw className="h-3 w-3" />
                            Actualizar base
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 text-[11px] text-red-600 hover:text-red-700 dark:text-red-400"
                            onClick={() => setBaselineConfirm("delete")}
                            disabled={baselineBusy}
                          >
                            <Trash2 className="h-3 w-3" />
                            Eliminar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Fila 2: Métricas secundarias */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
                    <span className="text-muted-foreground">
                      FOB Total <span className="font-semibold text-foreground tabular-nums">{fmtCOP.format(Number(projectedResult?.fob_total_cop ?? 0))}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Gastos peso <span className="font-semibold text-foreground tabular-nums">{fmtCOP.format(Number(projectedResult?.gastos_peso_cop ?? 0))}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Gastos valor <span className="font-semibold text-foreground tabular-nums">{fmtCOP.format(Number(projectedResult?.gastos_valor_cop ?? 0))}</span>
                    </span>
                    {(() => {
                      const sf = Number(projectedResult?.shortfall_peso ?? 0) + Number(projectedResult?.shortfall_valor ?? 0);
                      return (
                        <span className="text-muted-foreground">
                          Shortfall <span className={cn("font-semibold tabular-nums", sf <= 0.02 ? "text-foreground" : "text-amber-600 dark:text-amber-400")}>{sf.toFixed(2)}</span>
                        </span>
                      );
                    })()}
                    {hasBaselineDisplay && (
                      <span className="text-muted-foreground">
                        Landed Base <span className="font-semibold text-foreground tabular-nums">{fmtCOP.format(baselineTotal)}</span>
                      </span>
                    )}
                  </div>

                  {/* Fila 2.5: IVA proyectado + Desembolso total (flujo de caja) */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
                    <span className="text-muted-foreground">
                      IVA proyectado <span className="text-[10px] opacity-70">(recuperable)</span>{" "}
                      <span className="font-semibold tabular-nums text-muted-foreground">{fmtCOP.format(projectedIvaTotal)}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Desembolso total <span className="font-semibold tabular-nums text-foreground">{fmtCOP.format(projectedDesembolso)}</span>
                    </span>
                  </div>

                  {/* Fila 3: TRM */}
                  {projectedResult?.trm_source && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {typeof projectedResult.trm_aduana_usada === "number" && projectedResult.trm_aduana_usada > 0 && (
                        <span className="tabular-nums">USD <span className="font-semibold text-foreground">{fmtCOP.format(projectedResult.trm_aduana_usada).replace("$", "$ ")}</span></span>
                      )}
                      {(() => {
                        // FIX TRM-DISPLAY — usar trmMap efectivo (con overrides del Centro
                        // de Monedas) para que la etiqueta refleje la TRM realmente aplicada
                        // por el motor de costeo, no la TRM de mercado.
                        const baseMap = hydratedCostEngineInput?.trmMap ?? {};
                        const effMap: Record<string, number> = { ...baseMap };
                        for (const [code, rate] of Object.entries(trmOverrides ?? {})) {
                          const k = code.toUpperCase();
                          if (k === "COP") continue;
                          if (Number.isFinite(rate) && Number(rate) > 0) effMap[k] = Number(rate);
                        }
                        const monedas = new Set<string>();
                        for (const it of hydratedCostEngineInput?.items ?? []) {
                          const ov = itemsEditados[it.id];
                          const m = ((ov?.moneda ?? (it as { moneda?: string | null }).moneda) ?? "USD").toString().toUpperCase();
                          if (m && m !== "COP" && m !== "USD") monedas.add(m);
                        }
                        for (const it of itemsNuevos) {
                          const m = (it.moneda ?? "USD").toString().toUpperCase();
                          if (m && m !== "COP" && m !== "USD") monedas.add(m);
                        }
                        return Array.from(monedas).sort().map((m) => {
                          const rate = Number(effMap[m] ?? 0);
                          if (!(rate > 0)) return <span key={m} className="tabular-nums text-amber-600 dark:text-amber-400">{m} sin TRM</span>;
                          return (
                            <span key={m} className="tabular-nums">
                              {m} <span className="font-semibold text-foreground">{fmtCOP.format(rate).replace("$", "$ ")}</span>
                            </span>
                          );
                        });
                      })()}
                      {projectedResult.trm_source === "aduana" ? (
                        trmIsOutdated ? (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-amber-500 text-amber-700 dark:text-amber-400">
                            TRM desactualizada {trmAduanaDate ? `(${formatTrmDate(trmAduanaDate)})` : ""}
                          </Badge>
                        ) : (
                          <Badge variant="default" className="h-5 px-1.5 text-[10px] bg-emerald-600 hover:bg-emerald-600">
                            TRM final {trmAduanaDate ? `(${formatTrmDate(trmAduanaDate)})` : ""}
                          </Badge>
                        )
                      ) : (
                        <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-sky-500 text-sky-700 dark:text-sky-400">
                          TRM mercado{trmAduanaDate ? ` (${formatTrmDate(trmAduanaDate)})` : ""}
                        </Badge>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 ml-0.5"
                            onClick={() => setMonedaSheetOpen(true)}
                            aria-label="Editar TRM por moneda"
                          >
                            <Settings2 className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          Editar TRM por moneda
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              {/* Header de la simulación */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{current.nombre}</CardTitle>
                      <div className="text-xs mt-1 text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        {current.payload?.embarque_base?.numero_do && (
                          <>
                            <span>
                              Base:{" "}
                              <span className="font-medium text-foreground">
                                {current.payload.embarque_base.numero_do}
                              </span>
                            </span>
                            {current.payload.embarque_base.proveedor && (
                              <span>· {current.payload.embarque_base.proveedor}</span>
                            )}
                          </>
                        )}
                        <span>·</span>
                        <span>{itemsCount} ítems</span>
                        <span>·</span>
                        <span className="tabular-nums">{gastosCount} gastos</span>
                        {(editedCount > 0 || nuevosCount > 0 || itemDirtyCount > 0 || itemsNuevosCount > 0) && (
                          <Badge
                            variant="outline"
                            className="h-4 px-1.5 text-[10px] border-primary/30 text-primary"
                          >
                            {(editedCount + itemDirtyCount) > 0 && `${editedCount + itemDirtyCount} edit.`}
                            {(editedCount + itemDirtyCount) > 0 && (nuevosCount + itemsNuevosCount) > 0 && " · "}
                            {(nuevosCount + itemsNuevosCount) > 0 && `+${nuevosCount + itemsNuevosCount} nuevo${(nuevosCount + itemsNuevosCount) === 1 ? "" : "s"}`}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        onClick={handleSave}
                        disabled={updateMut.isPending}
                        size="sm"
                        className="gap-1.5"
                      >
                        {updateMut.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                        Guardar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onExportClick}
                        disabled={exportBusy || !projectedResult}
                        className="gap-1.5"
                      >
                        {exportBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        Exportar a Excel
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* ── Cotización Comercial — colapsable ── */}
              <Collapsible open={comercialOpen} onOpenChange={setComercialOpen}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 px-4 py-2 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Package className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-semibold shrink-0">Cotización comercial</span>
                        {!comercialOpen && (
                          <span className="text-[11px] text-muted-foreground truncate">
                            · {INCOTERM_LABELS[comercial.incoterm]} {comercial.puerto_carga ? `${comercial.puerto_carga} → ${comercial.puerto_destino ?? ""}` : ""} · {comercial.transito_dias ?? 0}d tránsito · {comercial.validez_dias ?? 0}d validez
                          </span>
                        )}
                      </div>
                      {comercialOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-3 pt-2">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs">Incoterm</Label>
                          <Select
                            value={comercial.incoterm}
                            onValueChange={(v) => setComercial((c) => ({ ...c, incoterm: v as Incoterm }))}
                          >
                            <SelectTrigger className="h-8 text-[13px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(Object.keys(INCOTERM_LABELS) as Incoterm[]).map((k) => (
                                <SelectItem key={k} value={k}>{INCOTERM_LABELS[k]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Puerto de carga</Label>
                          <Input value={comercial.puerto_carga ?? ""} onChange={(e) => setComercial((c) => ({ ...c, puerto_carga: e.target.value }))} className="h-8 text-[13px]" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Puerto de destino</Label>
                          <Input value={comercial.puerto_destino ?? ""} onChange={(e) => setComercial((c) => ({ ...c, puerto_destino: e.target.value }))} className="h-8 text-[13px]" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Tránsito (días)</Label>
                          <Input type="number" min={0} value={comercial.transito_dias ?? 0} onChange={(e) => setComercial((c) => ({ ...c, transito_dias: Number(e.target.value) }))} className="h-8 text-[13px] tabular-nums" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Días libres en destino</Label>
                          <Input type="number" min={0} value={comercial.dias_libres_destino ?? 0} onChange={(e) => setComercial((c) => ({ ...c, dias_libres_destino: Number(e.target.value) }))} className="h-8 text-[13px] tabular-nums" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Tiempo nacionalización (días)</Label>
                          <Input type="number" min={0} value={comercial.tiempo_nacionalizacion_dias ?? 0} onChange={(e) => setComercial((c) => ({ ...c, tiempo_nacionalizacion_dias: Number(e.target.value) }))} className="h-8 text-[13px] tabular-nums" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Validez (días)</Label>
                          <Input type="number" min={0} value={comercial.validez_dias ?? 0} onChange={(e) => setComercial((c) => ({ ...c, validez_dias: Number(e.target.value) }))} className="h-8 text-[13px] tabular-nums" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Condiciones de pago</Label>
                        <Input value={comercial.condiciones_pago ?? ""} onChange={(e) => setComercial((c) => ({ ...c, condiciones_pago: e.target.value }))} className="h-8 text-[13px]" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Observaciones</Label>
                        <Textarea value={comercial.observaciones ?? ""} onChange={(e) => setComercial((c) => ({ ...c, observaciones: e.target.value }))} rows={3} className="text-[13px]" placeholder="Notas que aparecerán en la cotización exportada…" />
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t">
                        <span className="text-[11px] text-muted-foreground">
                          Plantilla {comercial.incoterm}: {PRESETS_BY_INCOTERM[comercial.incoterm].length} gastos predefinidos.
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="gap-1.5 h-8"
                          onClick={() => {
                            const presets = PRESETS_BY_INCOTERM[comercial.incoterm];
                            const norm = (s: string) => s.toLowerCase().trim();
                            const existing = new Set<string>([
                              ...effectiveBaseGastos.map((g) => norm(g.concepto ?? "")),
                              ...gastosNuevos.map((g) => norm(g.concepto ?? "")),
                            ]);
                            const filtered = presets.filter((p) => !existing.has(norm(p.concepto)));
                            const skipped = presets.length - filtered.length;
                            const nuevos: SimulatedGasto[] = filtered.map((p) => ({
                              id: generateClientUUID(),
                              concepto: p.concepto,
                              monto: p.monto_default,
                              iva_local: 0,
                              moneda: p.moneda,
                              base_prorrateo: p.base_prorrateo === "Ninguna" ? "Ninguna" : p.base_prorrateo,
                              aplica_costeo_inventario: true,
                            }));
                            if (nuevos.length === 0) {
                              toast({ title: "Plantilla ya cargada", description: "Todos los gastos de la plantilla ya están agregados." });
                              return;
                            }
                            setGastosNuevos((prev) => [...prev, ...nuevos]);
                            if (skipped > 0) {
                              toast({ title: `Plantilla ${comercial.incoterm} cargada parcialmente`, description: `${nuevos.length} nuevo${nuevos.length === 1 ? "" : "s"}. ${skipped} ya existía${skipped === 1 ? "" : "n"}.` });
                            } else {
                              toast({ title: `Plantilla ${comercial.incoterm} cargada`, description: `${nuevos.length} gasto${nuevos.length === 1 ? "" : "s"} agregado${nuevos.length === 1 ? "" : "s"} al escenario.` });
                            }
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Cargar plantilla {comercial.incoterm}
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* ENG-01 R3: error del motor server-side */}
              {calcErrorVisible && (
                <Alert variant="destructive" className="relative pr-10">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>No se pudo recalcular el escenario</AlertTitle>
                  <AlertDescription>{calcError!.message}</AlertDescription>
                  <button
                    type="button"
                    aria-label="Cerrar"
                    onClick={() => setDismissedErrorMsg(calcError!.message)}
                    className="absolute right-2 top-2 rounded-md p-1 text-destructive/70 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Alert>
              )}

              {/* Modo Avanzado y fuente TRM movidos al ItemManagerSheet / KPI ribbon */}

              {/* ── Tablas gemelas: Ítems + Gastos ── */}
              <div className={cn(
                "grid grid-cols-1 lg:grid-cols-2 gap-3 transition-opacity",
                isCalculating && "opacity-60",
              )}>
                {/* Ítems */}
                <Card>
                  <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      Ítems
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{unitRows.length}</Badge>
                    </CardTitle>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] gap-1.5" onClick={() => setItemSheetOpen(true)}>
                      Editar ítems
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0">
                    {unitRows.length === 0 ? (
                      <div className="px-4 py-6 text-center text-xs text-muted-foreground">Sin ítems</div>
                    ) : (
                      <div className="max-h-[180px] overflow-y-auto border-t">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="text-[10px] uppercase tracking-wider">SKU / Producto</TableHead>
                              <TableHead className="text-right text-[10px] uppercase tracking-wider">Cant.</TableHead>
                              <TableHead className="text-right text-[10px] uppercase tracking-wider">FOB Unit.</TableHead>
                              <TableHead className="text-right text-[10px] uppercase tracking-wider">Landed Unit.</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {unitRows.map((r) => (
                              <TableRow key={r.itemId}>
                                <TableCell className="py-1 max-w-[220px]">
                                  <div className="truncate text-[12px] font-medium">{r.sku && r.sku !== "—" ? r.sku : r.descripcion}</div>
                                </TableCell>
                                <TableCell className="text-right tabular-nums py-1 text-[12px]">{r.cantidad.toLocaleString("es-CO")}</TableCell>
                                <TableCell className="text-right tabular-nums py-1 text-[12px] text-muted-foreground">
                                  {r.fobUnitario > 0 ? r.fobUnitario.toLocaleString("es-CO", { maximumFractionDigits: 2 }) : "—"}
                                </TableCell>
                                <TableCell className="text-right tabular-nums py-1 text-[12px] font-medium">
                                  {r.landedProjUnit != null ? fmtCOP.format(r.landedProjUnit) : "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Gastos */}
                <Card>
                  <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Settings2 className="h-4 w-4 text-primary" />
                      Gastos
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{gastosCount}</Badge>
                    </CardTitle>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] gap-1.5" onClick={() => setExpenseSheetOpen(true)}>
                      Editar gastos
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0">
                    {(() => {
                      const allGastos = [
                        ...effectiveBaseGastos.map((g) => {
                          const ov = gastosEditados[g.id ?? ""] ?? {};
                          return {
                            id: g.id ?? `b-${g.concepto}`,
                            concepto: g.concepto,
                            monto: Number(ov.monto ?? g.monto ?? 0),
                            moneda: (g.moneda ?? "COP") as string,
                            base_prorrateo: (ov.base_prorrateo ?? g.base_prorrateo ?? "Ninguna") as string,
                          };
                        }),
                        ...gastosNuevos.map((g) => ({
                          id: g.id,
                          concepto: g.concepto,
                          monto: Number(g.monto ?? 0),
                          moneda: g.moneda as string,
                          base_prorrateo: g.base_prorrateo as string,
                        })),
                      ];
                      if (allGastos.length === 0) return <div className="px-4 py-6 text-center text-xs text-muted-foreground">Sin gastos</div>;
                      return (
                        <div className="max-h-[180px] overflow-y-auto border-t">
                          <Table>
                            <TableHeader>
                              <TableRow className="hover:bg-transparent">
                                <TableHead className="text-[10px] uppercase tracking-wider">Concepto</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-wider">Scope</TableHead>
                                <TableHead className="text-right text-[10px] uppercase tracking-wider">Monto</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-wider">Base</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {allGastos.map((g) => {
                                const sc = inferScope(g.concepto);
                                const scopeColor = sc === "origen" ? "border-sky-500/40 text-sky-700 dark:text-sky-400 bg-sky-500/5"
                                  : sc === "internacional" ? "border-purple-500/40 text-purple-700 dark:text-purple-400 bg-purple-500/5"
                                  : "border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/5";
                                return (
                                  <TableRow key={g.id}>
                                    <TableCell className="py-1 max-w-[180px]">
                                      <div className="truncate text-[12px] font-medium">{g.concepto}</div>
                                    </TableCell>
                                    <TableCell className="py-1">
                                      <Badge variant="outline" className={cn("h-4 px-1.5 text-[9px] uppercase", scopeColor)}>{sc}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums py-1 text-[12px]">
                                      <span className="font-medium">{g.monto.toLocaleString("es-CO", { maximumFractionDigits: 2 })}</span>
                                      <span className="text-muted-foreground text-[10px] ml-1">{g.moneda}</span>
                                    </TableCell>
                                    <TableCell className="py-1 text-[11px] text-muted-foreground">{g.base_prorrateo}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>

              {/* ── Análisis de Impacto Unitario (SKU) — siempre visible ── */}
              <Card>
                <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Análisis de Impacto Unitario (SKU)
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{unitRows.length} ítems</Badge>
                  </CardTitle>
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-[11px]" onClick={() => setItemSheetOpen(true)}>
                    <Package className="h-3 w-3" /> Editar Ítems
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {unitRows.length === 0 ? (
                    <div className="px-6 py-8 text-center text-xs text-muted-foreground border-t">
                      No hay ítems en el snapshot.
                    </div>
                  ) : (
                    <div className="border-t overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-[28%] text-[10px] uppercase tracking-wider font-semibold">Producto / SKU</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">Cant.</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">FOB Unit.</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">Gastos asig.</TableHead>
                            {hasBaselineDisplay && (
                              <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">Landed Base</TableHead>
                            )}
                            <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">Landed Proyec.</TableHead>
                            {hasBaselineDisplay && (
                              <TableHead className="text-right text-[10px] uppercase tracking-wider font-semibold tabular-nums">Δ Unit.</TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {impactPagination.paginate(unitRows).map((r) => {
                            const isBad = (r.deltaUnit ?? 0) > 0.5;
                            const isGood = (r.deltaUnit ?? 0) < -0.5;
                            const deltaPctUnit = (r.landedBaseUnit ?? 0) > 0 && r.deltaUnit != null
                              ? (r.deltaUnit / r.landedBaseUnit!) * 100
                              : 0;
                            return (
                              <TableRow key={r.itemId}>
                                <TableCell className="max-w-[280px] py-1">
                                  <div className="font-medium text-foreground truncate text-[12px]">
                                    {r.sku && r.sku !== "—" ? r.sku : r.descripcion}
                                  </div>
                                  {r.sku && r.sku !== "—" && (
                                    <div className="text-[10px] text-muted-foreground truncate">{r.descripcion}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums py-1 text-[12px]">
                                  {r.cantidad.toLocaleString("es-CO")}
                                </TableCell>
                                <TableCell className="text-right tabular-nums text-muted-foreground py-1 text-[12px]">
                                  {r.fobUnitario > 0 ? r.fobUnitario.toLocaleString("es-CO", { maximumFractionDigits: 2 }) : "—"}
                                </TableCell>
                                <TableCell className="text-right tabular-nums py-1 text-[12px]">
                                  {(() => {
                                    const allocs = allocationsByItem.get(r.itemId) ?? [];
                                    const totalAlloc = allocs.reduce((s, a) => s + a.montoCOP, 0);
                                    const perUnit = r.cantidad > 0 ? totalAlloc / r.cantidad : 0;
                                    if (totalAlloc <= 0) return <span className="text-muted-foreground">—</span>;
                                    return (
                                      <span className="inline-flex flex-col items-end gap-0 leading-tight">
                                        <span className="font-medium">{fmtCOP.format(totalAlloc)}</span>
                                        <span className="text-[10px] text-muted-foreground tabular-nums">{fmtCOP.format(perUnit)} /u</span>
                                      </span>
                                    );
                                  })()}
                                </TableCell>
                                {hasBaselineDisplay && (
                                  <TableCell className="text-right tabular-nums py-1 text-[12px]">
                                    {r.landedBaseUnit != null ? fmtCOP.format(r.landedBaseUnit) : <span className="text-muted-foreground">—</span>}
                                  </TableCell>
                                )}
                                <TableCell className="text-right tabular-nums font-medium py-1 text-[12px]">
                                  {r.landedProjUnit != null ? fmtCOP.format(r.landedProjUnit) : <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                {hasBaselineDisplay && (
                                  <TableCell
                                    className={`text-right tabular-nums font-semibold py-1 text-[12px] ${
                                      isBad ? "text-red-600 dark:text-red-400" : isGood ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                                    }`}
                                  >
                                    <div className="inline-flex items-center justify-end gap-1">
                                      {isBad && <ArrowUpRight className="h-3 w-3" />}
                                      {isGood && <ArrowDownRight className="h-3 w-3" />}
                                      {r.deltaUnit != null ? (
                                        <span>{r.deltaUnit >= 0 ? "+" : ""}{fmtCOP.format(r.deltaUnit)}</span>
                                      ) : r.snapshotStatus === "new" ? (
                                        <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-primary/40 text-primary">Nuevo</Badge>
                                      ) : r.snapshotStatus === "removed" ? (
                                        <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-red-500/40 text-red-600 dark:text-red-400">Eliminado</Badge>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </div>
                                    {r.deltaUnit != null && Math.abs(r.deltaUnit) > 0.5 && (
                                      <div className="text-[10px] font-normal opacity-80 tabular-nums">{fmtPct(deltaPctUnit)}</div>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      {unitRows.length > impactPagination.rowsPerPage && (
                        <TablePagination
                          currentPage={impactPagination.currentPage}
                          totalItems={unitRows.length}
                          rowsPerPage={impactPagination.rowsPerPage}
                          onPageChange={impactPagination.setCurrentPage}
                          onRowsPerPageChange={impactPagination.handleRowsPerPageChange}
                        />
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
    </TooltipProvider>
  );

  return (
    <main className="flex-1 p-6 overflow-hidden md:overflow-hidden">
      {isMobile ? (
        <div className="flex flex-col gap-6 h-full overflow-auto">
          {leftPane}
          {rightPane}
        </div>
      ) : (
        <ResizablePanelGroup
          direction="horizontal"
          className="h-[calc(100vh-7rem)] rounded-lg"
        >
          <ResizablePanel defaultSize={25} minSize={20} maxSize={40} className="pr-3">
            {leftPane}
          </ResizablePanel>
          <ResizableHandle
            withHandle
            className="bg-border/60 hover:bg-primary/40 active:bg-primary transition-colors data-[resize-handle-state=hover]:bg-primary/40 data-[resize-handle-state=drag]:bg-primary"
          />
          <ResizablePanel defaultSize={75} minSize={50} className="pl-3">
            {rightPane}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}

      <NewSimulationWizard
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        onCreated={(id) => selectSim(id)}
        uiMode={uiMode}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar simulación?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar simulación <strong className="text-foreground">"{deleteTarget?.nombre ?? "sin nombre"}"</strong>?
              Esta acción no se puede deshacer. La simulación es solo un escenario,
              no afecta a ningún embarque real.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Validación pre-export — advertencias no bloqueantes ── */}
      <AlertDialog
        open={exportWarnings !== null}
        onOpenChange={(v) => !v && setExportWarnings(null)}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Advertencias antes de exportar</AlertDialogTitle>
            <AlertDialogDescription>
              Detectamos {exportWarnings?.length ?? 0} situación(es) que conviene revisar.
              Puedes corregir los datos o exportar de todas formas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
            {exportWarnings?.map((w, i) => (
              <details
                key={`${w.code}-${i}`}
                className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/20 p-3"
              >
                <summary className="flex items-start gap-2 cursor-pointer text-sm font-medium text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  <span className="flex-1">{w.message}</span>
                </summary>
                {w.details.length > 0 && (
                  <ul className="mt-2 ml-6 list-disc text-xs text-muted-foreground space-y-0.5">
                    {w.details.map((d, j) => (
                      <li key={j} className="font-mono">{d}</li>
                    ))}
                  </ul>
                )}
              </details>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setExportWarnings(null)}>
              Corregir datos
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setExportWarnings(null);
                void handleExport();
              }}
            >
              Exportar de todas formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={baselineConfirm !== null}
        onOpenChange={(v) => !v && setBaselineConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {baselineConfirm === "update"
                ? "¿Reemplazar la línea base?"
                : "¿Eliminar la línea base?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {baselineConfirm === "update"
                ? "La línea base anterior se perderá y será reemplazada por el escenario actual."
                : "Ya no podrás comparar contra este escenario. Los ítems y gastos NO se eliminan."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={baselineBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={baselineConfirm === "update" ? writeSnapshot : handleDeleteBaseline}
              className={
                baselineConfirm === "delete"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : undefined
              }
              disabled={baselineBusy}
            >
              {baselineBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : null}
              {baselineConfirm === "update" ? "Reemplazar" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── S19.8 — Gestor Dinámico de Gastos ── */}
      {current?.payload?.cost_engine_input && (
        <ExpenseManagerSheet
          open={expenseSheetOpen}
          onOpenChange={setExpenseSheetOpen}
          baseGastos={effectiveBaseGastos as unknown as ProformaPayload["cost_engine_input"]["gastos"]}
          initialEditados={gastosEditados}
          initialNuevos={gastosNuevos}
          trmMap={(() => {
            const base = hydratedCostEngineInput?.trmMap ?? current.payload.cost_engine_input.trmMap ?? {};
            const factor = 1;
            const out: Record<string, number> = { COP: 1 };
            for (const k of Object.keys(base)) {
              if (k === "COP") continue;
              out[k] = Number(base[k] ?? 0) * factor;
            }
            // Fallback con TRM de mercado si baseline no tiene USD
            if (!(out.USD > 0) && Number(trmMarket ?? 0) > 0) {
              out.USD = Number(trmMarket) * factor;
            }
            return out;
          })()}
          effectiveTrmMap={(() => {
            const base = hydratedCostEngineInput?.trmMap ?? current.payload.cost_engine_input.trmMap ?? {};
            const factor = 1;
            const out: Record<string, number> = { COP: 1 };
            for (const k of Object.keys(base)) {
              if (k === "COP") continue;
              out[k] = Number(base[k] ?? 0) * factor;
            }
            if (!(out.USD > 0) && Number(trmMarket ?? 0) > 0) {
              out.USD = Number(trmMarket) * factor;
            }
            for (const [code, rate] of Object.entries(trmOverrides ?? {})) {
              const k = code.toUpperCase();
              if (k === "COP") continue;
              if (Number.isFinite(rate) && Number(rate) > 0) out[k] = Number(rate);
            }
            return out;
          })()}
          onApply={({ gastosEditados: ed, gastosNuevos: nv }) => {
            setGastosEditados(ed);
            setGastosNuevos(nv.map((g) => ({ ...g, id: ensureUuid(g.id), iva_local: Number(g.iva_local ?? 0) })));
            setDismissedErrorMsg(null);
          }}
          onRemoveBase={removeGasto}
        />
      )}

      {/* ── Centro de Monedas ── */}
      <MonedaSheet
        open={monedaSheetOpen}
        onOpenChange={setMonedaSheetOpen}
        trmMap={latestTrmMap}
        initialOverrides={trmOverrides}
        monedas={(() => {
          const set = new Set<string>();
          for (const it of effectiveBaseItems) {
            const m = ((it as { moneda?: string | null }).moneda ?? "").toString().toUpperCase();
            if (m) set.add(m);
          }
          for (const it of itemsNuevos) {
            const m = (it.moneda ?? "").toString().toUpperCase();
            if (m) set.add(m);
          }
          for (const g of effectiveBaseGastos) {
            const m = (g.moneda ?? "").toString().toUpperCase();
            if (m) set.add(m);
          }
          for (const g of gastosNuevos) {
            const m = (g.moneda ?? "").toString().toUpperCase();
            if (m) set.add(m);
          }
          if (set.size === 0) set.add("USD");
          return Array.from(set);
        })()}
        onApply={(ov) => {
          setTrmOverrides(ov);
          setDismissedErrorMsg(null);
        }}
      />

      {/* ── S19.10 — Gestor Dinámico de Ítems ── */}
      {current?.payload?.cost_engine_input && (
        <ItemManagerSheet
          open={itemSheetOpen}
          onOpenChange={setItemSheetOpen}
          uiMode={uiMode}
          onUiModeChange={handleSetUiMode}
          baseItems={effectiveBaseItems}
          hostDocumentoId={current.payload.cost_engine_input.items?.[0]?.documento_id ?? current.payload.cost_engine_input.documents?.[0]?.id ?? null}
          baseArancelMap={(() => {
            // BUG 2 fix — fusionar overrides + arancel resuelto por RPC.
            // Esto permite que ítems nuevos (sin override) muestren el % arancel
            // que el RPC obtuvo del lookup `arancel_1881` por hs_code.
            const fromOverrides = current.payload.cost_engine_input.compliance?.arancelPctByItemId ?? {};
            const fromRpc: Record<string, number> = {};
            for (const it of projectedResult?.items ?? []) {
              fromRpc[it.id] = Number(it.arancel_pct ?? 0);
            }
            return { ...fromRpc, ...fromOverrides };
          })()}
          baseIvaMap={(() => {
            const fromOverrides = current.payload.cost_engine_input.compliance?.ivaPctByItemId ?? {};
            const fromRpc: Record<string, number> = {};
            for (const it of projectedResult?.items ?? []) {
              fromRpc[it.id] = Number(it.iva_pct ?? 0);
            }
            return { ...fromRpc, ...fromOverrides };
          })()}
          trmEfectivaMap={(() => {
            const base = hydratedCostEngineInput?.trmMap ?? current.payload.cost_engine_input.trmMap ?? {};
            const factor = 1;
            const out: Record<string, number> = { COP: 1 };
            for (const k of Object.keys(base)) {
              if (k === "COP") continue;
              out[k] = Number(base[k] ?? 0) * factor;
            }
            if (!(out.USD > 0) && Number(trmMarket ?? 0) > 0) {
              out.USD = Number(trmMarket) * factor;
            }
            return out;
          })()}
          initialEditados={itemsEditados}
          initialNuevos={itemsNuevos}
          initialArancelOverrides={arancelOverrides}
          initialIvaOverrides={ivaOverrides}
          initialBeneficioOverrides={beneficioOverrides}
          onApply={({ itemsEditados: ed, itemsNuevos: nv, arancelOverrides: ar, ivaOverrides: iv, beneficioOverrides: bo }) => {
            setItemsEditados(ed);
            setItemsNuevos(nv);
            setArancelOverrides(ar);
            setIvaOverrides(iv);
            setBeneficioOverrides(bo);
          }}
        />
      )}
    </main>
  );
}
